import {
  arrow,
  autoUpdate,
  flip,
  offset,
  Placement,
  safePolygon,
  shift,
  useClick,
  useDelayGroupContext,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole
} from "@floating-ui/react";
import { useState, useMemo, useRef } from "react";

export interface PopoverOptions {
  initialOpen?: boolean;
  placement?: Placement;
  modal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  popoverOffset?: number;
  enableHover?: boolean;
  enableClick?: boolean;
  keyboardHandlers?: boolean;
}

export const usePopover = ({
  initialOpen = false,
  placement = "bottom",
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  popoverOffset,
  enableHover = false,
  enableClick = true,
  keyboardHandlers
}: PopoverOptions) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();
  const arrowRef = useRef(null);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(popoverOffset ?? 10),
      flip({ fallbackAxisSideDirection: "end" }),
      shift({ padding: 10 }),
      arrow({ element: arrowRef })
    ]
  });

  const context = data.context;
  const { delay } = useDelayGroupContext();

  const click = useClick(context, {
    enabled: enableClick,
    keyboardHandlers
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const hover = useHover(context, {
    enabled: enableHover,
    handleClose: safePolygon({
      requireIntent: true
    }),
    delay
  });

  const interactions = useInteractions([click, hover, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      arrowRef
    }),
    [open, setOpen, interactions, data, modal, labelId, descriptionId, arrowRef]
  );
};
